"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { Check } from "lucide-react";

import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/use-auth";
import { useSubscription } from "@/hooks/use-subscription";
import { useToast } from "@/hooks/use-toast";
import { fetchWithAuth } from "@/lib/api-client";
import * as analytics from "@/lib/analytics";
import { cn } from "@/lib/utils";

// Paid tiers map to the existing Stripe-wired plan IDs ("standard", "pro").
// Do not change these ids — they drive checkout.
//
// Every feature listed here is backed by a shipped code path (the Business
// tier was hidden on 2026-08-25 until that was true — keep it that way:
// nothing goes on a card before it works).
const PAID_PLANS = [
  {
    id: "standard" as const,
    name: "Pro",
    price: "$49",
    cadence: "per month",
    description:
      "For professionals who want the law explained, monitored, and on file.",
    features: [
      "Everything in Free",
      "Plain-language explanation of every rule returned",
      "Each explanation pinned to the key passage it rests on",
      "Save up to 5 addresses with daily change monitoring",
      "Email digest when a tracked rule changes",
      "Lookup history",
      "Higher lookup limits than anonymous use",
    ],
    highlight: true,
    cta: "Subscribe",
  },
  {
    id: "pro" as const,
    name: "Business",
    price: "$149",
    cadence: "per month",
    description:
      "For portfolios: monitor many addresses and hand clients the report.",
    features: [
      "Everything in Pro",
      "Monitor up to 50 addresses",
      "Bulk lookups — paste a whole address list",
      "Printable compliance-snapshot reports",
    ],
    highlight: false,
    cta: "Upgrade",
  },
];

export default function PricingPage() {
  const router = useRouter();
  const { user, signOut } = useAuth();
  const { data, loading: subscriptionLoading, refresh } = useSubscription();
  const { showToast } = useToast();
  const [processingPlan, setProcessingPlan] = useState<string | null>(null);

  const isActive = data?.status === "active";
  const isTrialExpired = data?.status === "expired";

  const handleUpgrade = async (plan: string) => {
    if (!user) {
      router.push("/login");
      return;
    }
    if (isActive) {
      router.push("/billing");
      return;
    }

    setProcessingPlan(plan);
    analytics.trackSubscriptionEvent("upgrade", plan);
    analytics.trackButtonClick("upgrade_plan", "pricing");

    try {
      const response = await fetchWithAuth("/api/billing/create-checkout-session", {
        method: "POST",
        body: JSON.stringify({ plan }),
      });
      if (response.status === 401) {
        await signOut();
        return;
      }
      const payload = await response.json();
      if (!response.ok || !payload.url) {
        showToast(payload.error ?? "Unable to start checkout. Please try again.");
        analytics.trackEvent("subscription_checkout_error", { plan, error: payload.error });
        return;
      }
      showToast("Redirecting to checkout…");
      analytics.trackEvent("subscription_checkout_started", { plan });
      window.location.href = payload.url;
    } catch {
      showToast("Unable to start checkout. Please try again.");
      analytics.trackEvent("subscription_checkout_error", { plan });
    } finally {
      setProcessingPlan(null);
      void refresh();
    }
  };

  function paidCtaLabel(planId: string) {
    if (processingPlan === planId) return "Redirecting to Stripe…";
    if (isActive && data?.plan === planId) return "Current plan";
    if (isActive) return "Manage in billing";
    return PAID_PLANS.find((p) => p.id === planId)?.cta ?? "Subscribe";
  }

  return (
    <div className="mx-auto w-full max-w-6xl px-4 py-16 sm:px-6 sm:py-20">
      <div className="mx-auto max-w-2xl text-center">
        <p className="overline">Pricing</p>
        <h1 className="mt-2 font-serif text-display-sm font-semibold text-ink">
          Free for individuals. Funded by businesses.
        </h1>
        <p className="mx-auto mt-4 max-w-prose text-body-lg text-muted">
          Anyone can look up an address and see the rules that govern it, with a
          source for every layer — at no cost. Paid plans add plain-language
          explanations, monitoring, and reports. Every new account starts with a
          7-day free trial of Pro — no card required.
        </p>
        {subscriptionLoading ? (
          <p className="mt-4 text-body-sm text-muted">Checking your status…</p>
        ) : (
          <>
            {isActive && (
              <p className="mt-4 text-body-sm text-success">
                You&apos;re subscribed. Manage your plan on the billing page.
              </p>
            )}
            {isTrialExpired && (
              <p className="mt-4 text-body-sm text-warn">
                Your trial has ended. Choose a plan below to continue.
              </p>
            )}
          </>
        )}
      </div>

      <div className="mt-12 grid gap-5 md:grid-cols-3">
        {/* Free tier — no checkout. */}
        <div className="flex flex-col panel p-7">
          <div>
            <div className="flex items-baseline justify-between">
              <h2 className="text-h3 text-ink">Free</h2>
              <p className="font-medium text-ink">
                $0 <span className="text-body-sm font-normal text-muted">forever</span>
              </p>
            </div>
            <p className="mt-3 text-body-sm text-muted">
              For individuals checking the rules on a home, a move, or a project.
            </p>
            <ul className="mt-6 space-y-2.5">
              {[
                "Address → jurisdiction lookups",
                "The full federal-to-local stack",
                "A source link for every layer",
              ].map((f) => (
                <FeatureItem key={f}>{f}</FeatureItem>
              ))}
            </ul>
          </div>
          <div className="mt-7 flex-1 flex items-end">
            <Button asChild variant="outline" className="w-full">
              <Link href={user ? "/dashboard" : "/signup"}>
                {user ? "Go to lookup" : "Get started free"}
              </Link>
            </Button>
          </div>
        </div>

        {/* Paid tiers — Stripe-wired. */}
        {PAID_PLANS.map((plan) => {
          const isCurrent = isActive && data?.plan === plan.id;
          return (
            <div
              key={plan.id}
              className={cn(
                "flex flex-col panel p-7",
                plan.highlight && "ring-1 ring-primary border-primary",
              )}
            >
              <div>
                {plan.highlight && (
                  // "Most popular" was a popularity claim with nothing behind
                  // it — the honesty bar applies to badges too.
                  <span className="badge badge-success mb-3">Recommended</span>
                )}
                <div className="flex items-baseline justify-between">
                  <h2 className="text-h3 text-ink">{plan.name}</h2>
                  <p className="font-medium text-ink">
                    {plan.price}{" "}
                    <span className="text-body-sm font-normal text-muted">
                      {plan.cadence}
                    </span>
                  </p>
                </div>
                <p className="mt-3 text-body-sm text-muted">{plan.description}</p>
                <ul className="mt-6 space-y-2.5">
                  {plan.features.map((f) => (
                    <FeatureItem key={f}>{f}</FeatureItem>
                  ))}
                </ul>
              </div>
              <div className="mt-7 flex-1 flex items-end">
                <Button
                  className="w-full"
                  variant={plan.highlight ? "default" : "outline"}
                  onClick={() => handleUpgrade(plan.id)}
                  disabled={processingPlan === plan.id || isCurrent}
                >
                  {paidCtaLabel(plan.id)}
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Enterprise / API band. */}
      <div className="mt-6 panel p-7 sm:p-8">
        <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
          <div className="max-w-prose">
            <h2 className="text-h3 text-ink">Enterprise &amp; API</h2>
            <p className="mt-2 text-body text-muted">
              Programmatic access to the jurisdiction resolver for compliance,
              proptech, and government teams. Enterprise agreements are what keep
              individual lookups free — businesses fund open access to public-record
              information for everyone else.
            </p>
          </div>
          <Button asChild variant="outline" size="lg" className="shrink-0">
            <Link href="/support">Contact sales</Link>
          </Button>
        </div>
      </div>

      <p className="mt-8 text-center text-body-sm text-muted">
        Prosa provides legal information for orientation, not legal advice.
      </p>
    </div>
  );
}

function FeatureItem({ children }: { children: React.ReactNode }) {
  return (
    <li className="flex items-start gap-2.5 text-body-sm text-text">
      <Check className="mt-0.5 h-4 w-4 shrink-0 text-success" />
      <span>{children}</span>
    </li>
  );
}
